import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import type { Treatment } from "@/types/schema";
import type { Database } from "@/integrations/supabase/types";
import treatmentIndex from "@/data/generated/treatments-index.json";
import { useMemo } from "react";

export type TreatmentCategory = Database["public"]["Enums"]["treatment_category"];

// Fetch only NEW treatments since build
const fetchNewTreatments = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("treatments")
    .select("*")
    .gt("created_at", lastBuildDate) // Treatments usually don't have published_at? Checking schema if needed.
    .order("is_featured", { ascending: false });

  if (error) {
    console.error("Error fetching new treatments:", error);
    return null;
  }
  return data as Treatment[];
};

export function useTreatments() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<Treatment>({
    resourceName: "Treatments",
    staticData: treatmentIndex as unknown as Treatment[], // Casting because index might be partial
    fetchDynamic: fetchNewTreatments,
    queryKey: ["treatments"],
    sortFn: (a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0), // Basic sort, refine if needed
    skipUpdates: true,
  });

  return {
    data,
    isLoading: false,
    isStatic,
    isCheckingUpdates
  };
}

export function useTreatmentsByCategory(category: TreatmentCategory | undefined) {
  const { data } = useTreatments();

  const filtered = useMemo(() => {
    if (!category) return [];
    // Assuming 'All' might be passed or just specific categories
    return data?.filter((t) => t.category === category) || [];
  }, [data, category]);

  return {
    data: filtered,
    isLoading: false, 
  };
}

